import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, of, shareReplay } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { environment } from '@environments/environment';
import { ApiResponse } from '@core/models/general.model';
import {
  BusquedaGlobalApi,
  conteosVacios,
  esMotivoPropio,
  FiltroBusquedaApi,
  GrupoResultados,
  MIN_CARACTERES_BUSQUEDA,
  PaginaBusqueda,
  paginaVacia,
  ResultadoBusqueda,
  ResultadoBusquedaApi,
  TIPOS_RESULTADO,
} from '@shared/models/busqueda-global.model';

/** Vida de una entrada de caché. */
const TTL_MS = 60_000;

/** Tope de entradas en memoria antes de desalojar la más vieja. */
const MAX_ENTRADAS = 50;

interface EntradaCache {
  ts: number;
  data$: Observable<PaginaBusqueda>;
}

/**
 * Buscador global de proveedores. Consume `GET v1/proveedores/busqueda`, que
 * resuelve en UN solo SP las cuatro familias (despacho, orden de compra,
 * importación, tarifa) dentro del alcance (empresa, proveedor) del usuario.
 *
 * El error nunca se propaga: se convierte en página vacía con `error` para que
 * el desplegable del navbar no rompa la barra si el gateway está caído.
 */
@Injectable({ providedIn: 'root' })
export class BusquedaGlobalService {
  private readonly http = inject(HttpClient);
  private readonly transloco = inject(TranslocoService);
  private readonly API_URL = environment.apiUrl;
  private readonly BASE = 'v1/proveedores/busqueda';

  /**
   * Caché en memoria. La clave incluye EL ALCANCE (empresa + proveedor), no solo
   * el término: sin eso, servir las filas de otro alcance sería un fallo de
   * aislamiento, no una simple obsolescencia. También el idioma, porque las
   * etiquetas de fase llegan traducidas.
   */
  private readonly cache = new Map<string, EntradaCache>();

  consultar(filtro: FiltroBusquedaApi): Observable<PaginaBusqueda> {
    const termino = (filtro.q ?? '').trim();

    // Sin alcance (navbar aún sin empresas / tras logout) o con menos de 3
    // letras no hay viaje al backend: el panel muestra el estado vacío.
    if (
      termino.length < MIN_CARACTERES_BUSQUEDA ||
      filtro.empresaId === null ||
      !Number.isFinite(filtro.empresaId) ||
      filtro.proveedorId === null ||
      !Number.isFinite(filtro.proveedorId)
    ) {
      return of(paginaVacia());
    }

    const clave = this.clave({ ...filtro, q: termino });
    const ahora = Date.now();
    const enCache = this.cache.get(clave);
    if (enCache && ahora - enCache.ts < TTL_MS) {
      return enCache.data$;
    }

    const data$ = this.http
      .get<ApiResponse<BusquedaGlobalApi>>(`${this.API_URL}/${this.BASE}`, {
        params: this.toParams({ ...filtro, q: termino }),
      })
      .pipe(
        map((res) => this.mapPagina(res?.data)),
        catchError((err) => {
          // Un error no se cachea: si se guardara, el usuario quedaría con el
          // fallo pegado durante el TTL aunque el backend ya se hubiera repuesto.
          this.cache.delete(clave);
          return of(paginaVacia(this.mensajeError(err)));
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    this.cache.set(clave, { ts: ahora, data$ });
    this.podar();
    return data$;
  }

  /** Vacía la caché. */
  limpiarCache(): void {
    this.cache.clear();
  }

  // ── Interno ───────────────────────────────────────────────────────────────

  private clave(f: FiltroBusquedaApi): string {
    return JSON.stringify([
      f.q.toLowerCase(),
      f.empresaId,
      f.proveedorId,
      this.transloco.getActiveLang(),
      f.topPorTipo ?? null,
    ]);
  }

  /** Desaloja la entrada más antigua cuando el mapa pasa del tope. */
  private podar(): void {
    while (this.cache.size > MAX_ENTRADAS) {
      const masVieja = [...this.cache.entries()].reduce((a, b) =>
        a[1].ts <= b[1].ts ? a : b,
      );
      this.cache.delete(masVieja[0]);
    }
  }

  private toParams(f: FiltroBusquedaApi): HttpParams {
    let params = new HttpParams()
      .set('q', f.q)
      .set('empresaId', String(f.empresaId))
      .set('proveedorId', String(f.proveedorId));

    if (f.topPorTipo !== undefined)
      params = params.set('topPorTipo', String(f.topPorTipo));

    return params;
  }

  private mapPagina(data: BusquedaGlobalApi | undefined): PaginaBusqueda {
    const items = (data?.items ?? []).map((r) => this.mapFila(r));

    const conteos = conteosVacios();
    for (const c of data?.conteos ?? []) {
      if (c.tipo in conteos) conteos[c.tipo] = c.total ?? 0;
    }

    const grupos: GrupoResultados[] = TIPOS_RESULTADO.map((tipo) => ({
      tipo,
      total: conteos[tipo],
      items: items.filter((i) => i.tipo === tipo),
    })).filter((g) => g.items.length > 0);

    return {
      items,
      grupos,
      totalCount: data?.totalCount ?? 0,
      conteos,
    };
  }

  private mapFila(r: ResultadoBusquedaApi): ResultadoBusqueda {
    return {
      tipo: r.tipo,
      id: r.entidadID,
      // Hay despachos sin NroDespacho; el consecutivo es el respaldo visible.
      numero: r.numero?.trim() || r.consecutivo?.trim() || '—',
      consecutivo: r.consecutivo?.trim() || null,
      empresaId: r.empresaID ?? null,
      empresa: r.empresaNombre?.trim() || null,
      cliente: r.cliente?.trim() || null,
      abierto: r.estadoAbierto ?? true,
      fase: r.faseEtiqueta?.trim() || null,
      faseColor: r.faseColor?.trim() || null,
      fecha: r.fecha ? new Date(r.fecha) : null,
      relacion: r.relacion?.trim() || null,
      motivo: r.motivoCoincidencia,
      valorCoincidencia: r.valorCoincidencia?.trim() || null,
      coincidenciaIndirecta: !esMotivoPropio(r.motivoCoincidencia),
    };
  }

  private mensajeError(err: unknown): string {
    const status = (err as { status?: number })?.status;
    return status === 0
      ? this.transloco.translate('BUSQUEDA.ERRORS.SIN_CONEXION')
      : this.transloco.translate('BUSQUEDA.ERRORS.GENERICO');
  }
}
